import React, { useState } from "react";
import { Rocket, Shield, File, Cookie, ArrowLeft } from "lucide-react";
import { Link, useParams, useNavigate } from "react-router-dom";
import config from "../../config";

const Legal = () => {
  const { policyType = "terms" } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(policyType);

  const handleTabChange = (tab: any) => {
    setActiveTab(tab);
    navigate(`/legal/${tab}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              <Rocket className="h-6 w-6 text-purple-600" />
              <span className="font-bold text-xl text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500">
                GoViralStrategy
              </span>
            </Link>
          </div>
          <Link
            to="/"
            className="flex items-center text-gray-600 hover:text-purple-600 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b">
            <button
              className={`px-6 py-4 text-sm font-medium ${
                activeTab === "terms"
                  ? "text-purple-600 border-b-2 border-purple-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => handleTabChange("terms")}
            >
              <div className="flex items-center">
                <File className="h-4 w-4 mr-2" />
                Terms of Service
              </div>
            </button>
            <button
              className={`px-6 py-4 text-sm font-medium ${
                activeTab === "privacy"
                  ? "text-purple-600 border-b-2 border-purple-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => handleTabChange("privacy")}
            >
              <div className="flex items-center">
                <Shield className="h-4 w-4 mr-2" />
                Privacy Policy
              </div>
            </button>
            <button
              className={`px-6 py-4 text-sm font-medium ${
                activeTab === "cookies"
                  ? "text-purple-600 border-b-2 border-purple-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => handleTabChange("cookies")}
            >
              <div className="flex items-center">
                <Cookie className="h-4 w-4 mr-2" />
                Cookies Policy
              </div>
            </button>
          </div>

          {/* Content */}
          <div className="p-6 sm:p-8">
            {activeTab === "terms" && <TermsOfService />}
            {activeTab === "privacy" && <PrivacyPolicy />}
            {activeTab === "cookies" && <CookiesPolicy />}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Last updated: April 3, 2025</p>
          <p className="mt-2">© 2025 GoViralStrategy. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

const TermsOfService = () => (
  <div className="space-y-6">
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-4">
        Terms of Service
      </h1>
      <p className="text-gray-600">
        Welcome to GoViralStrategy. These Terms of Service govern your use of
        our website and services.
      </p>
    </div>

    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        1. Acceptance of Terms
      </h2>
      <p className="text-gray-600">
        By accessing or using GoViralStrategy, you agree to be bound by these
        Terms of Service. If you disagree with any part of the terms, you may
        not access our services.
      </p>
    </div>

    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        2. Description of Service
      </h2>
      <p className="text-gray-600">
        GoViralStrategy provides AI-powered strategy recommendations for
        creators on short-form video platforms like TikTok, Instagram Reels, and
        YouTube Shorts. Our services may change from time to time without prior
        notice.
      </p>
    </div>

    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        3. User Accounts
      </h2>
      <p className="text-gray-600">
        When you create an account, you are responsible for maintaining the
        security of your account and for all activities that occur under your
        account. You must provide accurate and complete information. You may not
        use false or misleading information.
      </p>
    </div>

    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        4. Subscription and Billing
      </h2>
      <p className="text-gray-600">
        Some of our services are offered on a subscription basis. You will be
        billed in advance on a recurring basis. You may cancel your subscription
        at any time, but we do not provide refunds for the current billing
        cycle.
      </p>
    </div>

    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        5. Content Ownership
      </h2>
      <p className="text-gray-600">
        GoViralStrategy does not claim ownership of your content. However, by
        uploading content, you grant us a license to use, modify, and display
        that content in connection with providing our services to you.
      </p>
    </div>

    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        6. Prohibited Activities
      </h2>
      <p className="text-gray-600">
        Users may not engage in any activity that interferes with the proper
        working of the service, attempts to bypass any security measures, or
        violates any laws.
      </p>
    </div>

    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        7. Limitation of Liability
      </h2>
      <p className="text-gray-600">
        GoViralStrategy is provided on an "as is" and "as available" basis. We
        make no guarantees that our services will meet your specific
        requirements or expectations.
      </p>
    </div>

    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        8. Changes to Terms
      </h2>
      <p className="text-gray-600">
        We reserve the right to modify these terms at any time. We will provide
        notice of significant changes by posting a prominent announcement on our
        website.
      </p>
    </div>

    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        9. Contact Information
      </h2>
      <p className="text-gray-600">
        If you have any questions about these Terms, please contact us at{" "}
        {config.emails.team}.
      </p>
    </div>
  </div>
);

const PrivacyPolicy = () => (
  <div className="space-y-6">
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
      <p className="text-gray-600">
        At GoViralStrategy, we take your privacy seriously. This Privacy Policy
        explains how we collect, use, and protect your personal information.
      </p>
    </div>

    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        1. Information We Collect
      </h2>
      <p className="text-gray-600">
        We collect information you provide directly to us, such as your name,
        email address, and content information when you register for an account
        or use our services. We also collect certain information automatically,
        including your IP address and device information.
      </p>
    </div>

    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        2. How We Use Your Information
      </h2>
      <p className="text-gray-600">
        We use your information to provide, maintain, and improve our services,
        process transactions, send communications, and for analytical purposes
        to better understand how our services are used.
      </p>
    </div>

    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        3. Information Sharing
      </h2>
      <p className="text-gray-600">
        We do not sell your personal information. We may share your information
        with third-party service providers who help us deliver our services,
        comply with legal obligations, or protect our rights.
      </p>
    </div>

    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        4. Data Security
      </h2>
      <p className="text-gray-600">
        We implement appropriate technical and organizational measures to
        protect your personal information against unauthorized access,
        alteration, disclosure, or destruction.
      </p>
    </div>

    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        5. Your Data Rights
      </h2>
      <p className="text-gray-600">
        Depending on your location, you may have the right to access, correct,
        delete, or restrict the processing of your personal information. You may
        also have the right to data portability and to withdraw consent.
      </p>
    </div>

    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        6. Children's Privacy
      </h2>
      <p className="text-gray-600">
        Our services are not intended for individuals under the age of 16. We do
        not knowingly collect personal information from children under 16.
      </p>
    </div>

    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        7. Changes to This Policy
      </h2>
      <p className="text-gray-600">
        We may update this Privacy Policy from time to time. We will notify you
        of any changes by posting the new Privacy Policy on this page and
        updating the "Last Updated" date.
      </p>
    </div>

    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        8. Contact Us
      </h2>
      <p className="text-gray-600">
        If you have any questions about this Privacy Policy, please contact us
        at {config.emails.team}.
      </p>
    </div>
  </div>
);

const CookiesPolicy = () => (
  <div className="space-y-6">
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Cookies Policy</h1>
      <p className="text-gray-600">
        This Cookies Policy explains how GoViralStrategy uses cookies and
        similar technologies to recognize and remember you when you visit our
        website.
      </p>
    </div>

    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        1. What Are Cookies
      </h2>
      <p className="text-gray-600">
        Cookies are small text files that are stored on your browser or device
        by websites, apps, online media, and advertisements. They are used to
        remember your preferences and help the website function properly.
      </p>
    </div>

    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        2. Types of Cookies We Use
      </h2>
      <div className="text-gray-600 space-y-2">
        <p>
          <strong>Essential Cookies:</strong> These cookies are necessary for
          the website to function properly and cannot be switched off.
        </p>
        <p>
          <strong>Performance Cookies:</strong> These cookies allow us to count
          visits and traffic sources so we can measure and improve the
          performance of our site.
        </p>
        <p>
          <strong>Functional Cookies:</strong> These cookies enable the website
          to provide enhanced functionality and personalization.
        </p>
        <p>
          <strong>Targeting Cookies:</strong> These cookies may be set by our
          advertising partners to build a profile of your interests and show you
          relevant advertisements.
        </p>
      </div>
    </div>

    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        3. How We Use Cookies
      </h2>
      <p className="text-gray-600">
        We use cookies to understand how you interact with our website, remember
        your preferences, and improve your browsing experience. We also use
        cookies to help us compile aggregate data about site traffic and
        interaction so that we can enhance our website and services.
      </p>
    </div>

    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        4. Cookie Management
      </h2>
      <p className="text-gray-600">
        Most web browsers allow you to control cookies through their settings.
        You can set your browser to refuse all cookies or to indicate when a
        cookie is being sent. However, some features of our website may not
        function properly without cookies.
      </p>
    </div>

    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        5. Third-Party Cookies
      </h2>
      <p className="text-gray-600">
        We may use third-party services, such as Google Analytics, which use
        cookies to help us analyze how users use our website. These third
        parties may use cookies, web beacons, and other storage technologies to
        collect information about your use of our website.
      </p>
    </div>

    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        6. Updates to This Policy
      </h2>
      <p className="text-gray-600">
        We may update this Cookie Policy from time to time. We will notify you
        of any changes by posting the new Cookie Policy on this page.
      </p>
    </div>

    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        7. Contact Us
      </h2>
      <p className="text-gray-600">
        If you have any questions about our use of cookies, please contact us at{" "}
        {config.emails.team}.
      </p>
    </div>
  </div>
);

export default Legal;
